import { MessageComposeFields } from '@/features/messaging/MessageComposeFields';
import { describeAccount, readComposeDraftFields } from '@/features/messaging/message-compose-shared';
import {
  EMPTY_DRAFT_FIELDS,
  draftFieldsFromLists,
  splitRecipients,
  useMessageDraftLifecycle,
  type DraftFields,
} from '@/features/messaging/useMessageDraftLifecycle';
import { contentKeys } from '@/hooks/useContentQueries';
import { useMemberExternalAccounts } from '@/hooks/useMemberExternalAccountQueries';
import { useUser } from '@/hooks';
import { useComposerStore } from '@/store/useComposerStore';
import { useViewContextStore } from '@/store/viewContextStore';
import { getAxiosErrorDisplayMessage } from '@/utils/error-utils';
import { useQueryClient } from '@tanstack/react-query';
import type { ComposeDraftProjection, Message } from '@vivport/backend/types';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

interface MessageDraftPageProps {
  content: Message;
  composeDraft: ComposeDraftProjection;
}

export function MessageDraftPage({ content, composeDraft }: MessageDraftPageProps) {
  const queryClient = useQueryClient();
  const { data: memberContext } = useUser();
  const memberId = memberContext?.selectedMember.id ?? 0;
  const accountsQuery = useMemberExternalAccounts(memberId);
  const account =
    (accountsQuery.data?.items ?? []).find((item) => item.id === composeDraft.memberExternalAccountId) ?? null;

  const [fields, setFields] = useState<DraftFields>(() => draftFieldsFromLists(composeDraft));
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const overlayComposerOpen = useComposerStore((s) => s.isOpen);

  useEffect(() => {
    if (overlayComposerOpen) return;
    useViewContextStore.getState().setSlot('messageComposer', {
      kind: 'compose',
      draftContentId: content.id,
      unsaved: {
        to: fields.to,
        cc: fields.cc,
        bcc: fields.bcc,
        subject: fields.subject,
        body: fields.authoredBody,
      },
    });
    return () => useViewContextStore.getState().clearSlot('messageComposer');
  }, [overlayComposerOpen, content.id, fields.to, fields.cc, fields.bcc, fields.subject, fields.authoredBody]);

  const create = useCallback(
    async (): Promise<{ draftContentId: number; fields: DraftFields }> => ({
      draftContentId: content.id,
      fields: draftFieldsFromLists(composeDraft),
    }),
    [content.id, composeDraft]
  );

  const lifecycle = useMessageDraftLifecycle({
    flushKey: `draft-page-${content.id}`,
    active: true,
    fields,
    baseline: EMPTY_DRAFT_FIELDS,
    draftContentId: content.id,
    dirty,
    onBound: () => undefined,
    onDirtyChange: setDirty,
    onReplaceFields: setFields,
    create,
    readRemoteFields: readComposeDraftFields,
  });

  const patch = (next: Partial<DraftFields>): void => {
    if (error) setError(null);
    setFields((current) => ({ ...current, ...next }));
    setDirty(true);
    lifecycle.noteEdit();
  };

  const canSend = account !== null && splitRecipients(fields.to).length > 0 && !isSending;

  const handleSend = async (): Promise<void> => {
    if (!canSend) return;
    setError(null);
    setIsSending(true);
    try {
      await lifecycle.send();
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: contentKeys.detail(content.id) }),
        queryClient.invalidateQueries({ queryKey: contentKeys.lists() }),
        queryClient.invalidateQueries({
          predicate: (q) => Array.isArray(q.queryKey) && q.queryKey[0] === 'content-ref',
        }),
      ]);
      toast.success('Message sent');
    } catch (sendError) {
      const message = getAxiosErrorDisplayMessage(sendError, 'Failed to send message.');
      setError(message);
      toast.error(message);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <section aria-label="Email draft editor" className="rounded-lg border bg-background">
      <div className="flex items-center justify-between gap-2 rounded-t-lg border-b bg-muted/40 px-3 py-2">
        <span className="text-sm font-medium">Draft</span>
        {account ? (
          <span className="truncate text-xs text-muted-foreground" title={describeAccount(account)}>
            {describeAccount(account)}
          </span>
        ) : null}
      </div>
      <MessageComposeFields
        fields={fields}
        disabled={isSending}
        onChange={patch}
        onBlur={lifecycle.saveNow}
        onSend={() => void handleSend()}
        canSend={canSend}
        isSending={isSending}
        error={error}
        revision={lifecycle.revision}
        bodyRows={14}
      />
    </section>
  );
}
